/**
 * CanvasThemeScope (spec section 14.2 items 2, 4, 7).
 *
 * Wraps a canvas's rendered subtree and injects its `theme_config` CSS variables
 * scoped to the canvas root element. Scoping and cleanup are structural: the
 * variables are set as inline custom properties on this one wrapper element, so
 *  - they apply to the whole canvas subtree by normal custom-property inheritance,
 *  - within that subtree they override the org variables set on `html` and the
 *    brand base (spec 14.2 item 2: brand -> org -> canvas, achieved by DOM
 *    scoping -- the nearest ancestor that sets a custom property wins), and
 *  - they are removed automatically on unmount (item 4), because the element and
 *    its inline style are gone; nothing leaks to a sibling canvas or the shell.
 *
 * A canvas with no `theme_config` (and no wrapper className/style) emits no CSS
 * variables, so this component renders NO wrapper element at all -- it returns
 * its children directly (a React Fragment). That makes the no-config DOM
 * byte-identical to a canvas rendered without any theme scope (item 2, item 7
 * "no-config equivalence"): there is not even an extra div in the tree.
 */

import React, { useMemo } from "react";
import {
  canvasThemeVars,
  NormalizeOptions,
} from "./canvasThemeConfig";

export interface CanvasThemeScopeProps {
  /** The canvas `theme_config` (any shape; validated against the whitelist). */
  themeConfig?: unknown;
  /** The canvas-level `color` accent, fallback for an absent `palette.primary`. */
  color?: string;
  /** Sink for invalid-value fallback logs; defaults to console.warn. */
  logger?: NormalizeOptions["logger"];
  className?: string;
  /** Extra inline styles to merge (theme variables always win over these). */
  style?: React.CSSProperties;
  children: React.ReactNode;
  /** Test hook. */
  "data-testid"?: string;
}

/** Data attribute stamped on the scope element for identification and testing. */
export const CANVAS_THEME_SCOPE_ATTR = "data-canvas-theme-scope";

export const CanvasThemeScope: React.FC<CanvasThemeScopeProps> = ({
  themeConfig,
  color,
  logger,
  className,
  style,
  children,
  "data-testid": dataTestId,
}) => {
  const vars = useMemo(
    () => canvasThemeVars(themeConfig, { color, logger }),
    [themeConfig, color, logger],
  );

  const hasVars = Object.keys(vars).length > 0;

  // True pass-through: when the canvas contributes no theme variables and the
  // caller supplied no wrapper styling or test hook, render children directly
  // with no extra DOM node. This is what makes no-config rendering byte-identical
  // to a canvas with no theme scope at all (spec 14.2 item 7).
  if (!hasVars && !style && !className && dataTestId === undefined) {
    return <>{children}</>;
  }

  // Custom properties are not in React.CSSProperties; merge then cast.
  const mergedStyle = { ...(style ?? {}), ...vars } as React.CSSProperties;

  const attrs = { [CANVAS_THEME_SCOPE_ATTR]: "" } as Record<string, string>;

  return (
    <div
      {...attrs}
      className={className}
      style={mergedStyle}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
};

export default CanvasThemeScope;
